import { useEffect, useState } from "react";
import { Bell, BellRing, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/format";
import { urlBase64ToUint8Array } from "@/lib/push";
import { Button } from "@/components/ui/button";

// Enable/disable Web Push reminders (24h + 1h before appointments) + send a test notification.
export default function PushToggle() {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setSubscribed(!!sub))
      .catch(() => {});
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Permesso notifiche negato dal browser"); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/public-key");
      if (!data.public_key) { toast.error("Notifiche push non configurate sul server"); return; }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      const j = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: j.endpoint, keys: j.keys, ua: navigator.userAgent });
      setSubscribed(true);
      toast.success("Notifiche attive: riceverai i promemoria 24h e 1h prima di ogni appuntamento");
    } catch (e) {
      toast.error("Impossibile attivare le notifiche su questo dispositivo");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      const endpoint = sub?.endpoint;
      if (sub) await sub.unsubscribe();
      await api.delete("/push/subscribe", { data: { endpoint } });
      setSubscribed(false);
      toast.success("Notifiche disattivate");
    } catch (e) {
      toast.error("Errore durante la disattivazione delle notifiche");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      await api.post("/push/test");
      toast.success("Notifica di prova inviata: controlla il dispositivo");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Impossibile inviare la notifica di prova");
    } finally { setBusy(false); }
  };

  if (!supported) return null;

  return (
    <div className="flex items-center gap-1">
      <Button
        variant={subscribed ? "secondary" : "outline"}
        size="sm"
        onClick={subscribed ? disable : enable}
        disabled={busy}
        data-testid="push-toggle-button"
        className="gap-1.5"
        title={subscribed ? "Notifiche push attive (clic per disattivare)" : "Attiva le notifiche push per i promemoria"}
      >
        {subscribed ? <BellRing className="w-4 h-4 text-primary" /> : <Bell className="w-4 h-4" />}
        <span className="hidden sm:inline">{subscribed ? "Notifiche attive" : "Attiva notifiche"}</span>
      </Button>
      {subscribed && (
        <Button
          variant="ghost"
          size="icon"
          onClick={sendTest}
          disabled={busy}
          data-testid="push-test-button"
          className="h-8 w-8 text-muted-foreground hover:text-primary"
          title="Invia notifica di prova"
        >
          <Send className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}
